import React, { FC, useEffect, useRef } from 'react';
import { AttachmentCard } from 'types/Attachments';
import { Field, TextInput } from '@kontentino/ui';
import { useForm } from 'react-hook-form';
import { getFullPath, getHostname, prependHttp } from 'utils/url';
import _debounce from 'lodash.debounce';
import TimeUtils from 'app/utils/time';
import { useTranslation } from 'react-i18next';

type Props = {
  attachmentCard: AttachmentCard;
  onChange(card: Partial<AttachmentCard>): void;
  disabled?: boolean;
  withDescriptionField?: boolean;
  isFullLinkEditable?: boolean;
};

type Form = {
  url?: string;
  title: string;
  description: string;
  isFullLinkEditable?: boolean;
};

function getInitialPath(url: string) {
  let path = getFullPath(url);

  if (path.startsWith('/')) {
    path = path.slice(1);
  }

  return path;
}

const EditableLinkCardForm: FC<Props> = (props) => {
  const { t } = useTranslation();
  const { register, handleSubmit, setValue } = useForm<Form>({
    defaultValues: {
      description: props.attachmentCard.description,
      url: props.isFullLinkEditable
        ? props.attachmentCard.url
        : getInitialPath(props.attachmentCard.url),
      title: props.attachmentCard.title,
    },
  });

  const onSubmit = handleSubmit((values: Form) => {
    props.onChange({
      ...values,

      url: props.isFullLinkEditable
        ? values.url?.length
          ? prependHttp(values.url)
          : ''
        : prependHttp(`${getHostname(props.attachmentCard.url)}/${values.url}`),
    });
  });

  const onDebouncedChange = useRef<() => void>(
    _debounce(() => {
      onSubmit();
    }, TimeUtils.calcMilliseconds.seconds(0.5)),
  );

  useEffect(() => {
    setValue(
      'url',
      props.isFullLinkEditable
        ? props.attachmentCard.url
        : getInitialPath(props.attachmentCard.url),
    );
    setValue('title', props.attachmentCard.title);
    setValue('description', props.attachmentCard.description);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.attachmentCard.url]);

  return (
    <form
      onBlur={onSubmit}
      onChange={onDebouncedChange.current}
      className="tw-flex tw-flex-col tw-gap-4"
      data-cy="post-composer_carousel-card-form"
    >
      <Field.Group>
        <Field.Label required>{t('title')}</Field.Label>
        <TextInput
          {...register('title')}
          placeholder={t('title')}
          disabled={props.disabled}
          data-cy="carousel-card-form_title"
        />
      </Field.Group>
      {props.withDescriptionField && (
        <Field.Group>
          <Field.Label required>{t('description')}</Field.Label>
          <TextInput
            {...register('description')}
            placeholder={t('description')}
            disabled={props.disabled}
            data-cy="carousel-card-form_description"
          />
        </Field.Group>
      )}
      <Field.Group>
        <Field.Label required>{t('link')}</Field.Label>
        <TextInput
          {...register('url')}
          prefix={
            props.isFullLinkEditable
              ? undefined
              : `${prependHttp(getHostname(props.attachmentCard.url))}/`
          }
          disabled={props.disabled}
          data-cy="carousel-card-form_url"
        />
      </Field.Group>
    </form>
  );
};

export default EditableLinkCardForm;
